import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { Fullscreen, FullscreenExit, OpenInNew, CallEnd } from '@mui/icons-material';
import {
  LiveKitRoom as LiveKitRoomBase,
  VideoConference,
  TrackToggle as TrackToggleBase,
  useRoomContext,
} from '@livekit/components-react';
import { DisconnectReason } from 'livekit-client';
import '@livekit/components-styles';

// @livekit/components-react's published types predate React 19's stricter JSX component return
// type (its components return `ReactNode`, which includes `undefined` — not assignable to
// `Element | null` under the newer typings). This is a transitional type-only mismatch, not a
// runtime issue; the cast below is the standard workaround until the library ships React-19-typed
// builds. Remove this cast when that lands.
const LiveKitRoom: React.ComponentType<any> = LiveKitRoomBase as any;
const TrackToggle: React.ComponentType<any> = TrackToggleBase as any;

// getDisplayMedia (screen capture) is not implemented by any mobile browser (iOS Safari/Chrome,
// Android Chrome/Firefox) as of writing — this is a platform limitation, not a bug: there is no
// web API a mobile browser exposes for it. Detected once at module load rather than per-render.
const screenShareSupported =
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function';

interface LiveKitRoomViewProps {
  token: string;
  serverUrl: string;
  /** Which project's room this is — threaded into the "open in new tab" link so the dedicated
   * page reconnects to the same project rather than defaulting to none. Omitted for rooms with no
   * dedicated full-page route (e.g. temporary meeting rooms) — "open in new tab" simply doesn't
   * render in that case, see below. */
  projectId?: number;
  /** @param voluntary true when the user clicked a Leave button (ours or VideoConference's own
   * built-in one — both end up calling room.disconnect(), which the SDK reports as
   * DisconnectReason.CLIENT_INITIATED, see below); false/undefined for an involuntary drop
   * (network loss, admin force-disconnect, room closed). Callers use this to decide whether to
   * auto-reconnect (only on involuntary drops) or show a "you left" state (on voluntary leave). */
  onDisconnected: (voluntary?: boolean) => void;
  /** Full-page mode (the dedicated /virtual-office tab) fills the viewport instead of a fixed
   * embedded card height, and hides the "open in new tab" action (you're already in one). */
  fullPage?: boolean;
}

// Only rendered as a child of <LiveKitRoom>, so useRoomContext() has a Room instance to read. A
// second, guaranteed-to-work Leave button alongside VideoConference's own built-in one — added
// because the built-in one was reported not reliably disconnecting, especially while fullscreen
// (see onLeave: exits fullscreen BEFORE disconnecting, since removing a fullscreened element from
// the DOM without calling exitFullscreen first can leave some browsers stuck showing a blank
// fullscreen surface, which is what "leave doesn't work in fullscreen" actually was).
const LeaveButton: React.FC<{ onLeave: () => void }> = ({ onLeave }) => {
  const room = useRoomContext();
  const handleLeave = () => {
    onLeave();
    room.disconnect();
  };
  return (
    <Button size="small" variant="contained" color="error" startIcon={<CallEnd fontSize="small" />} onClick={handleLeave}>
      Leave
    </Button>
  );
};

// Shared connected-room UI — used both by the embedded Attendance-page widget (LiveKitPresence)
// and the dedicated full-page /virtual-office tab (VirtualOfficePage), so both stay in sync.
//
// Screen share: LiveKit's <VideoConference /> prefab renders its OWN internal <ControlBar
// controls={{ chat: true, settings: ... }} /> that isn't configurable from outside (hardcoded in
// the library — see LIVEKIT_SETUP.md). Rather than depend on VideoConference's own screen-share
// button (which some browser/permission combinations were suppressing), this adds an explicit,
// always-rendered <TrackToggle source="screen_share"> in its own toolbar row above the call —
// guaranteed present regardless of what VideoConference decides to show. On a device with no
// getDisplayMedia support (any mobile browser) it's shown disabled with an explanatory tooltip
// instead of a button that would silently fail when clicked.
const LiveKitRoomView: React.FC<LiveKitRoomViewProps> = ({ token, serverUrl, projectId, onDisconnected, fullPage = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Belt-and-suspenders: if this view unmounts while still fullscreen for ANY reason (network
  // drop, VideoConference's own built-in Leave button, an admin force-disconnect) — not just our
  // LeaveButton above, which already exits fullscreen proactively — make sure fullscreen is
  // actually released rather than leaving the browser stuck showing a blank fullscreen surface.
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const exitFullscreenIfActive = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // room.disconnect() (called by our LeaveButton below AND by VideoConference's own built-in
  // Leave button — both go through the same Room API) reports DisconnectReason.CLIENT_INITIATED;
  // any other reason (network loss, admin force-disconnect, room closed) is involuntary. This is
  // the one signal that reliably covers both Leave buttons — the built-in one can't be intercepted
  // directly since VideoConference hardcodes its own ControlBar with no props exposed for it.
  const handleRoomDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      onDisconnected(reason === DisconnectReason.CLIENT_INITIATED);
    },
    [onDisconnected],
  );

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }, []);

  const openInNewTab = () => window.open(`/virtual-office?projectId=${projectId}`, '_blank', 'noopener,noreferrer');
  const canOpenInNewTab = !fullPage && projectId !== undefined;

  return (
    <Box
      ref={containerRef}
      data-lk-theme="default"
      sx={{
        height: fullPage ? '100%' : 520,
        bgcolor: 'black',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" spacing={0.5} sx={{ bgcolor: '#111', px: 1, py: 0.5, flexShrink: 0 }}>
        <Box sx={{ flexGrow: 1 }} />
        {canOpenInNewTab && (
          <Tooltip title="Open in new tab">
            <IconButton size="small" onClick={openInNewTab} sx={{ color: 'white' }}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <IconButton size="small" onClick={toggleFullscreen} sx={{ color: 'white' }}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        connect
        audio
        video={false}
        onDisconnected={handleRoomDisconnected}
        style={{ flexGrow: 1, minHeight: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5, bgcolor: '#1a1a1a' }}>
          {screenShareSupported ? (
            <TrackToggle source="screen_share">Share Screen</TrackToggle>
          ) : (
            <Tooltip title="Screen sharing isn't supported on this browser — most mobile browsers (iOS/Android) don't implement it. Try a desktop browser instead.">
              <span>
                {/* MUI's default disabled-button text is a low-opacity BLACK, meant for light
                    backgrounds — invisible against this toolbar's near-black bgcolor. Override
                    via the .Mui-disabled class rather than relying on the default. */}
                <Button
                  size="small"
                  disabled
                  variant="outlined"
                  sx={{
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.5)',
                      borderColor: 'rgba(255,255,255,0.23)',
                    },
                  }}
                >
                  Share Screen
                </Button>
              </span>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <LeaveButton onLeave={exitFullscreenIfActive} />
        </Stack>
        <VideoConference />
      </LiveKitRoom>
    </Box>
  );
};

export default LiveKitRoomView;
